import {BehaviorSubject, Subject, combineLatest, filter, map, switchMap, take} from 'rxjs';
import {divided, gt, gte, minus, plus, times, toFixed} from '@/helper/num';
import {mc} from '@/core';
import {lendingApi} from '@/api/lending';
import {isLogin$} from '@/streams/auth';
import {browser$} from '@/streams/browser';
import {currenciesCacheByCode$} from '@/streams/config/currency';
import {AnnualInterestRatePrecision, HourlyInterestRatePrecision, LTVPrecision, PoolLoanType} from './const';
import {convertDisplayPercentage, convertDisplayPercentageLtMin, convertDisplayValue} from './utils';

interface Query {
  currency: number;
  poolId: number;
}

const USDTCurrency = 'USDT';

export const loadLendingPools$ = new Subject<Query | void>();

const innerLendingPools$ = new BehaviorSubject([]);
export const lendingPools$ = new BehaviorSubject<LendingPool[]>([]);
export const lendingGeneralPools$ = new BehaviorSubject<LendingPool[]>([]);
export const lendingStakedPtPools$ = new BehaviorSubject<LendingPool[]>([]);
export const lendingUSDTVePtPool$ = new BehaviorSubject<LendingPool>(null);

const poolsOverviewDefault = {
  poolTotalBorrowedRq: '0',
  poolTotalBorrow: '--',
  totalLiquidityRq: '0',
  totalLiquidity: '--',
  totalAvailableRq: '0',
  totalAvailable: '--',
  totalMarketSizeRq: '0',
  totalMarketSize: '--',
  totalSuppliedRq: '0',
  totalSupplied: '--',
};
export const lendingGeneralPoolsOverview$ = new BehaviorSubject<LendingPoolsOverviewDefault>(poolsOverviewDefault);

loadLendingPools$.pipe(switchMap(getPool), filter(Boolean)).subscribe(innerLendingPools$);

browser$.pipe(take(1)).subscribe(() => {
  loadLendingPools$.next();
});

combineLatest({pools: innerLendingPools$, currenciesCache: currenciesCacheByCode$})
  .pipe(
    filter(({currenciesCache}) => currenciesCache.size > 0),
    map(({pools, currenciesCache}) => pools.map((pool) => convertPool(pool, currenciesCache)).filter(Boolean))
  )
  .subscribe((pools) => {
    const {generalPools, stakedPtPools} = pools.reduce(
      ({generalPools, stakedPtPools}, cur) => {
        if (cur.loanType === PoolLoanType.stakedPt) {
          return {generalPools, stakedPtPools: [...stakedPtPools, cur]};
        }
        return {generalPools: [...generalPools, cur], stakedPtPools};
      },
      {generalPools: [], stakedPtPools: []}
    );
    lendingPools$.next(pools);
    lendingGeneralPools$.next(generalPools);
    lendingStakedPtPools$.next(stakedPtPools);
    const lendingUSDTVePtPool = generalPools.find((o) => o.loanType === PoolLoanType.vePTDiscount && o.currencyInfo.currency === USDTCurrency);
    lendingUSDTVePtPool$.next(lendingUSDTVePtPool);
  });

lendingGeneralPools$.pipe(map(convertPoolsOverview)).subscribe(lendingGeneralPoolsOverview$);

export function convertSelectPools(pools: LendingPool[], borrowCurrency = null, poolId = null): LendingPool[] {
  return Array.from(
    pools
      .reduce((p, pool) => {
        if (pool.currencyInfo.currency === borrowCurrency && pool.poolId !== poolId) {
          return p;
        }
        if (!pool.canBorrow || pool.supportedCollaterals.length === 0) {
          return p;
        }
        if (p.has(pool.currency)) {
          const cur: LendingPool = p.get(pool.currency);
          if (pool.loanType !== cur.loanType) {
            if (pool.loanType === PoolLoanType.vePTDiscount && cur.loanType !== PoolLoanType.vePTDiscount) {
              return p.set(pool.currency, pool);
            }
            return p;
          }
          if (pool.loanType === PoolLoanType.vePTDiscount) {
            if (gte(pool.discountBorrowApyRr, cur.discountBorrowApyRr)) {
              return p;
            }
          } else if (gte(pool.borrowAPYRr, cur.borrowAPYRr)) {
            return p;
          }
        }
        return p.set(pool.currency, pool);
      }, new Map())
      .values()
  );
}

export function convertSupplySelectPools(pools: LendingPool[], currency = null, poolId = null): LendingPool[] {
  return Array.from(
    pools
      .reduce((p, pool) => {
        if (pool.currencyInfo.currency === currency && poolId && pool.poolId !== poolId) {
          return p;
        }
        if (!pool.canSupply) {
          return p;
        }
        if (p.has(pool.currency)) {
          const cur: LendingPool = p.get(pool.currency);
          if (gte(cur.supplyAPYRr, pool.supplyAPYRr)) {
            return p;
          }
        }
        return p.set(pool.currency, pool);
      }, new Map())
      .values()
  );
}

async function getPool() {
  const isLogin = isLogin$.getValue();
  const {data, error} = await (isLogin ? lendingApi.getPool : lendingApi.getPublicPool)();
  if (error) {
    mc.send('toast', 'error', error);
    return null;
  }
  return (data && data.rows) || null;
}

function convertPool(pool, currenciesCache: Map<number, CurrencyEntity>): LendingPool {
  const currencyInfo = currenciesCache.get(pool.currency);
  if (!currencyInfo) {
    return null;
  }

  const {
    poolId,
    currency,
    currentHourlyBorrowRate,
    borrowAPY,
    totalBorrowedRq,
    totalLiquidityRq,
    totalBorrowedWithDiscount,
    supportedCollaterals,
    optimalUsageRr,
    baseBorrowRate,
    variableRateSlope1,
    variableRateSlope2,
    discountRate,
    discountBudgetRate,
    discountableRatioVept,
    discountableRatioLoan,
    maxVeptAmount,
    minVeptAmount,
    minBorrowPerUser,
    maxBorrowPerUser,
    initLtvRr,
    marginCallL1LtvRr,
    liqLtvRr,
    poolOrder,
    poolLoanType: loanType,
    lastPrice,
    canBorrow,
    marketSize: marketSizeRq,
    canSupply,
    supplyAPY: supplyAPYRr,
    estSupplyAPY: estSupplyAPYRr,
    minSupplyPerUser: minSupplyPerUserRq,
    maxSupplyPerUser: maxSupplyPerUserRq,
    supplyCap: supplyCapRq,
  } = pool;

  const supplyCapUsdRq = supplyCapRq ? times(supplyCapRq, lastPrice || 0) : '';
  const totalLiquidityUsd = times(totalLiquidityRq || 0, lastPrice || 0);
  const poolTotalBorrowedRq = plus(totalBorrowedRq || 0, totalBorrowedWithDiscount || 0);
  const poolTotalBorrowedUsdRq = times(poolTotalBorrowedRq || 0, lastPrice || 0);
  const totalAvailableRq = minus(totalLiquidityRq || 0, poolTotalBorrowedRq || 0);
  const totalAvailableUsd = times(totalAvailableRq || 0, lastPrice || 0);

  // 利用率 (Utilization Rate）Ut 公式  = 借款总额 / 总资产池流动性
  const utilizationRateRr = gt(totalLiquidityRq, 0) ? toFixed(divided(poolTotalBorrowedRq, totalLiquidityRq), 4, 3) : '0';

  const discountBorrowApyRr = times(borrowAPY || 0, discountRate);
  const discountableRatioRr = divided(discountableRatioVept || '1', discountableRatioLoan || '1');

  const discountHourlyBorrowRateRr = times(currentHourlyBorrowRate || 0, discountRate);

  const offDiscountRr = minus(1, discountRate || '0');
  const hourSupplyRateRr = supplyAPYRr ? divided(supplyAPYRr, times(365, 24)) : '';

  return {
    poolId,
    currency,
    displayCurrency: currencyInfo.displayCurrency,
    currencyInfo,
    currentHourlyBorrowRateRr: currentHourlyBorrowRate || '0',
    currentHourlyBorrowRate: convertDisplayPercentage(currentHourlyBorrowRate, HourlyInterestRatePrecision, true, 3),
    borrowAPYRr: borrowAPY || '0',
    borrowAPY: convertDisplayPercentage(borrowAPY, AnnualInterestRatePrecision, true, 3),
    supplyAPYRr: supplyAPYRr || '0',
    supplyAPY: convertDisplayPercentageLtMin(supplyAPYRr, AnnualInterestRatePrecision),
    hourSupplyRateRr,
    hourSupplyRate: convertDisplayPercentageLtMin(hourSupplyRateRr, HourlyInterestRatePrecision),
    estSupplyAPYRr,
    estSupplyAPY: convertDisplayPercentageLtMin(estSupplyAPYRr, AnnualInterestRatePrecision),
    totalBorrowedRq: totalBorrowedRq || '0',
    totalBorrowed: convertDisplayValue(totalBorrowedRq, currencyInfo.valuePrecision),
    poolTotalBorrowedRq,
    poolTotalBorrowed: convertValueAbbreviation(poolTotalBorrowedRq, Math.min(currencyInfo.valuePrecision, 6)),
    poolTotalBorrowedUsdRq,
    poolTotalBorrowedUsd: convertValueAbbreviation(poolTotalBorrowedUsdRq, 2),
    totalLiquidityRq: totalLiquidityRq || '0',
    totalLiquidity: convertValueAbbreviation(totalLiquidityRq, currencyInfo.valuePrecision),
    totalLiquidityUsdRq: totalLiquidityUsd || '0',
    totalLiquidityUsd: convertValueAbbreviation(totalLiquidityUsd, 2),
    marketSizeRq,
    totalAvailableRq,
    totalAvailableUsdRq: totalAvailableUsd || '0',
    totalAvailableUsd: convertDisplayValue(totalAvailableUsd, 2),
    totalBorrowedWithDiscountRq: totalBorrowedWithDiscount || '0',
    totalBorrowedWithDiscount: convertDisplayValue(totalBorrowedWithDiscount, currencyInfo.valuePrecision),
    utilizationRateRr,
    utilizationRate: convertDisplayPercentage(utilizationRateRr, 2, true, 3),
    supportedCollaterals,
    optimalUsageRr: optimalUsageRr || '0',
    optimalUsage: convertDisplayPercentage(optimalUsageRr),
    baseBorrowRateRr: baseBorrowRate || '0',
    baseBorrowRate: convertDisplayPercentage(baseBorrowRate),
    variableRateSlope1Rr: variableRateSlope1 || '0',
    variableRateSlope1: convertDisplayPercentage(variableRateSlope1),
    variableRateSlope2Rr: variableRateSlope2 || '0',
    variableRateSlope2: convertDisplayPercentage(variableRateSlope2),
    discountRateRr: discountRate || '0',
    discountRate: convertDisplayPercentage(discountRate),
    offDiscountRr,
    offDiscount: convertDisplayPercentage(offDiscountRr),
    discountBorrowApyRr,
    discountBorrowApy: convertDisplayPercentage(discountBorrowApyRr, AnnualInterestRatePrecision, true, 3),
    discountHourlyBorrowRateRr,
    discountHourlyBorrowRate: convertDisplayPercentage(discountHourlyBorrowRateRr, HourlyInterestRatePrecision, true, 3),
    discountBudgetRateRr: discountBudgetRate || '0',
    discountBudgetRate: convertDisplayPercentage(discountBudgetRate),
    discountableRatioVePT: discountableRatioVept || '1',
    discountableRatioLoan: discountableRatioLoan || '1',
    discountableRatioRr,
    discountableRatio: convertDisplayPercentage(discountableRatioRr),
    maxVePTAmountRq: maxVeptAmount || '0',
    maxVePTAmount: convertDisplayValue(maxVeptAmount, currencyInfo.valuePrecision),
    minVePTAmountRq: minVeptAmount || '0',
    minVePTAmount: convertDisplayValue(minVeptAmount, currencyInfo.valuePrecision),
    minBorrowPerUserRq: minBorrowPerUser || '0',
    minBorrowPerUser: convertDisplayValue(minBorrowPerUser, currencyInfo.valuePrecision),
    maxBorrowPerUserRq: maxBorrowPerUser || '0',
    maxBorrowPerUser: convertDisplayValue(maxBorrowPerUser, currencyInfo.valuePrecision),
    initLtvRr: initLtvRr || '0',
    initLtv: convertDisplayPercentage(initLtvRr, LTVPrecision, true, 3),
    marginCallL1LtvRr: marginCallL1LtvRr || '0',
    marginCallL1Ltv: convertDisplayPercentage(marginCallL1LtvRr, LTVPrecision, true, 3),
    liqLtvRr: liqLtvRr || '0',
    liqLtv: convertDisplayPercentage(liqLtvRr, LTVPrecision, true, 3),
    poolOrder,
    loanType,
    lastPriceRq: lastPrice,
    lastPrice: convertDisplayValue(lastPrice, currencyInfo.valuePrecision),
    canBorrow,
    canSupply,
    minSupplyPerUserRq: minSupplyPerUserRq || '0',
    minSupplyPerUser: convertDisplayValue(minSupplyPerUserRq),
    maxSupplyPerUserRq: maxSupplyPerUserRq || '0',
    maxSupplyPerUser: convertDisplayValue(maxSupplyPerUserRq),
    supplyCapRq: supplyCapRq || '0',
    supplyCap: convertValueAbbreviation(supplyCapRq, Math.min(currencyInfo.valuePrecision, 6)),
    supplyCapUsdRq: supplyCapUsdRq || '0',
    supplyCapUsd: convertValueAbbreviation(supplyCapUsdRq, 2),
  };
}

function convertPoolsOverview(pools: LendingPool[]): LendingPoolsOverviewDefault {
  const {poolTotalBorrowedRq, totalLiquidityRq, totalAvailableRq, totalMarketSizeRq, totalSuppliedRq} = pools.reduce(
    ({poolTotalBorrowedRq, totalLiquidityRq, totalAvailableRq, totalMarketSizeRq, totalSuppliedRq}, c) => {
      return {
        poolTotalBorrowedRq: plus(poolTotalBorrowedRq, c.poolTotalBorrowedUsdRq),
        totalLiquidityRq: plus(totalLiquidityRq, c.totalLiquidityUsdRq),
        totalAvailableRq: plus(totalAvailableRq, c.totalAvailableUsdRq),
        totalMarketSizeRq: plus(totalMarketSizeRq, c.marketSizeRq || 0),
        totalSuppliedRq: plus(totalSuppliedRq, c.supplyCapRq || 0),
      };
    },
    {
      poolTotalBorrowedRq: '0',
      totalLiquidityRq: '0',
      totalAvailableRq: '0',
      totalMarketSizeRq: '0',
      totalSuppliedRq: '0',
    }
  );
  return {
    poolTotalBorrowedRq,
    poolTotalBorrow: convertValueAbbreviation(poolTotalBorrowedRq, 2),
    totalLiquidityRq,
    totalLiquidity: convertValueAbbreviation(totalLiquidityRq, 2),
    totalAvailableRq,
    totalAvailable: convertValueAbbreviation(totalAvailableRq, 2),
    totalMarketSizeRq,
    totalMarketSize: convertValueAbbreviation(totalMarketSizeRq, 2),
    totalSuppliedRq,
    totalSupplied: convertValueAbbreviation(totalSuppliedRq, 2),
  };
}

function convertValueAbbreviation(value: string | number, precision: number = 8) {
  if (!value && value !== 0) {
    return '--';
  }
  const configs = [
    {unit: 1000000000, text: 'B'},
    {unit: 1000000, text: 'M'},
    {unit: 1000, text: 'K'},
  ];
  const config = configs.find((item) => gte(value || 0, item.unit));
  if (!config) {
    return convertDisplayValue(value, precision);
  }
  return convertDisplayValue(divided(value, config.unit), precision) + config.text;
}

export function getPoolBorrowApy(pool: LendingPool) {
  return pool.loanType === PoolLoanType.vePTDiscount ? pool.discountBorrowApy : pool.borrowAPY;
}

export function getPoolHourlyBorrowRate(pool: LendingPool) {
  return pool.loanType === PoolLoanType.vePTDiscount ? pool.discountHourlyBorrowRate : pool.currentHourlyBorrowRate;
}

export function createInitPool(info = {}): LendingPool {
  return {
    poolId: null,
    currency: null,
    displayCurrency: '--',
    currencyInfo: null,
    currentHourlyBorrowRateRr: '0',
    currentHourlyBorrowRate: '--',
    borrowAPYRr: '0',
    borrowAPY: '--',
    supplyAPYRr: '0',
    supplyAPY: '--',
    hourSupplyRateRr: '0',
    hourSupplyRate: '--',
    estSupplyAPYRr: '0',
    estSupplyAPY: '--',
    totalBorrowedRq: '0',
    totalBorrowed: '--',
    poolTotalBorrowedRq: '0',
    poolTotalBorrowed: '--',
    poolTotalBorrowedUsdRq: '0',
    poolTotalBorrowedUsd: '--',
    totalLiquidityRq: '0',
    totalLiquidity: '--',
    totalLiquidityUsdRq: '0',
    totalLiquidityUsd: '--',
    marketSizeRq: '0',
    totalAvailableRq: '0',
    totalAvailableUsdRq: '0',
    totalAvailableUsd: '--',
    totalBorrowedWithDiscountRq: '0',
    totalBorrowedWithDiscount: '--',
    utilizationRateRr: '0',
    utilizationRate: '--',
    supportedCollaterals: [],
    optimalUsageRr: '0',
    optimalUsage: '--',
    baseBorrowRateRr: '0',
    baseBorrowRate: '0',
    variableRateSlope1Rr: '0',
    variableRateSlope1: '--',
    variableRateSlope2Rr: '0',
    variableRateSlope2: '--',
    discountRateRr: '0',
    discountRate: '--',
    offDiscountRr: '0',
    offDiscount: '--',
    discountBorrowApyRr: '0',
    discountBorrowApy: '--',
    discountHourlyBorrowRateRr: '0',
    discountHourlyBorrowRate: '--',
    discountBudgetRateRr: '0',
    discountBudgetRate: '--',
    discountableRatioVePT: '1',
    discountableRatioLoan: '1',
    discountableRatioRr: '1',
    discountableRatio: '1',
    maxVePTAmountRq: '0',
    maxVePTAmount: '--',
    minVePTAmountRq: '0',
    minVePTAmount: '--',
    minBorrowPerUserRq: '0',
    minBorrowPerUser: '--',
    maxBorrowPerUserRq: '0',
    maxBorrowPerUser: '--',
    initLtvRr: '0',
    initLtv: '--',
    marginCallL1LtvRr: '0',
    marginCallL1Ltv: '--',
    liqLtvRr: '0',
    liqLtv: '--',
    poolOrder: 0,
    loanType: PoolLoanType.general,
    lastPriceRq: '--',
    lastPrice: '--',
    canBorrow: 1,
    canSupply: 1,
    minSupplyPerUserRq: '0',
    minSupplyPerUser: '--',
    maxSupplyPerUserRq: '0',
    maxSupplyPerUser: '--',
    supplyCapRq: '0',
    supplyCap: '--',
    supplyCapUsdRq: '0',
    supplyCapUsd: '--',
    ...info,
  };
}
